import React, { KeyboardEvent, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import { SvgIconComponent } from "@mui/icons-material";

interface InlineEditProps {
    value: string | null;
    displayValue?: string;
    Icon?: SvgIconComponent;
    iconElement?: React.ReactNode;
    startEditing?: boolean;
    handleSave: (newValue: string | null) => Promise<void>;
    renderEdit: (props: {
        value: string | null;
        onChange: (value: string | null) => void;
        onBlur: () => void;
        onKeyDown: (e: KeyboardEvent) => void;
    }) => React.ReactNode;
    emptyText?: string;
    className?: string;
}

export function InlineEdit({
    value,
    displayValue,
    Icon,
    iconElement,
    startEditing = false,
    handleSave,
    renderEdit,
    emptyText = "",
    className = "",
}: InlineEditProps) {
    const [editing, setEditing] = useState<boolean>(!!startEditing);
    const [loading, setLoading] = useState(false);
    const blurTimeoutRef = useRef<number | null>(null);

    useEffect(() => {
        if (startEditing) setEditing(true);
    }, [startEditing]);

    useEffect(() => {
        return () => {
            if (blurTimeoutRef.current) {
                window.clearTimeout(blurTimeoutRef.current);
                blurTimeoutRef.current = null;
            }
        };
    }, []);

    const scheduleClose = () => {
        if (blurTimeoutRef.current) {
            window.clearTimeout(blurTimeoutRef.current);
        }
        blurTimeoutRef.current = window.setTimeout(() => {
            // Don't close while a save is in-flight (e.g. option click triggers blur).
            if (!loading) setEditing(false);
        }, 150);
    };

    const handleChange = async(newValue: string | null) => {
        if (blurTimeoutRef.current) {
            window.clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
        }
        setLoading(true);
        try {
            await handleSave(newValue);
            setEditing(false);
        } finally {
            setLoading(false);
        }
    };

    if (editing) {
        return (
            <>
                <LoadingIndicator loading={loading} />
                {renderEdit({
                    value,
                    onChange: (v) => {
                        void handleChange(v);
                    },
                    onBlur: scheduleClose,
                    onKeyDown: (e) => {
                        if (e.key === "Escape") {
                            if (blurTimeoutRef.current) {
                                window.clearTimeout(blurTimeoutRef.current);
                                blurTimeoutRef.current = null;
                            }
                            setEditing(false);
                        }
                    },
                })}
            </>
        );
    }

    const shownText = !!value ? (displayValue ?? value) : emptyText;

    return (
        <Box
            className={`hover-effect ${className}`}
            sx={{ cursor: "pointer", display: "flex", alignItems: "center", maxWidth: "100%" }}
            onClick={(e) => {
                e.stopPropagation();
                setEditing(true);
            }}
        >
            {iconElement
                ? <span style={{ marginRight: 4, display: "inline-flex" }}>{iconElement}</span>
                : Icon && <Icon fontSize="small" color="primary" sx={{ mr: 0.5 }} />}

            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                    fontSize="small"
                    component="span"
                    noWrap
                    sx={{ display: "block" }}
                    fontWeight={!!value ? "600" : undefined}
                    fontStyle={!!value ? undefined : "italic"}
                    color={!!value ? undefined : "text.secondary"}
                >
                    {shownText}
                </Typography>
            </Box>
        </Box>
    );
}
